'use client';
import { Button } from '@material-tailwind/react';
import { useSpinner } from 'hooks/useSpinner';
import {
  ComponentProps,
  createContext,
  forwardRef,
  JSX,
  useCallback,
  useContext,
  useState,
} from 'react';

const FormSubmitting = createContext(false);

type FormOnSubmitEvent = Parameters<
  NonNullable<ComponentProps<'form'>['onSubmit']>
>[0];

export const LoaderForm = forwardRef<HTMLFormElement, ComponentProps<'form'>>(
  ({ onSubmit, ...props }, ref) => {
    const [loading, setLoading] = useState<boolean>(false);

    return (
      <FormSubmitting.Provider value={loading}>
        <form
          onSubmit={useCallback(
            (event: FormOnSubmitEvent) => {
              event.preventDefault();
              if (onSubmit == null) return;
              setLoading(true);
              Promise.resolve(onSubmit(event)).finally(() => setLoading(false));
            },
            [onSubmit],
          )}
          ref={ref}
          {...props}
        />
      </FormSubmitting.Provider>
    );
  },
);

LoaderForm.displayName = 'LoaderForm';

export function useFormLoading(): boolean {
  return useContext(FormSubmitting);
}

export function SubmitButton(
  props: ComponentProps<typeof Button>,
): JSX.Element {
  const state = useFormLoading();
  const loading = useSpinner(state);
  return (
    <Button
      {...props}
      type="submit"
      loading={props?.loading || loading}
      disabled={props?.disabled || state}
    >
      {loading ? null : props.children}
    </Button>
  );
}
